import { loadGuestSession } from "@/lib/guestAuth";
import { observeApiExchange, observeApiTransportError } from "@shared/serverReachability";
import { resolveTenantSlug } from "@shared/tenantSlug";

const HOST_API_BASE: Record<string, string> = {
  "dev.booking.campingplatz-mainkur.de": "https://dev.api.campingplatz-mainkur.de",
  "booking.campingplatz-mainkur.de": "https://api.campingplatz-mainkur.de",
  "test.booking.campingplatz-mainkur.de": "https://api.campingplatz-mainkur.de",
};

function resolveApiBase(hostname: string): string {
  const mapped = HOST_API_BASE[hostname];
  if (mapped) return mapped;
  const fromBuild = (import.meta.env.VITE_API_BASE as string | undefined)?.trim();
  if (fromBuild) return fromBuild.replace(/\/$/, "");
  if (hostname === "localhost" || hostname === "127.0.0.1") return "";
  if (hostname.startsWith("dev.booking.")) {
    return `https://dev.api.${hostname.slice("dev.booking.".length)}`;
  }
  if (hostname.startsWith("test.booking.")) {
    return `https://api.${hostname.slice("test.booking.".length)}`;
  }
  if (hostname.startsWith("booking.")) {
    return `https://api.${hostname.slice("booking.".length)}`;
  }
  return "";
}

export const API_BASE = (() => {
  if (typeof window === "undefined") return "";
  const fromMeta = document.querySelector('meta[name="api-base"]')?.getAttribute("content");
  if (fromMeta) return fromMeta.replace(/\/$/, "");
  return resolveApiBase(window.location.hostname);
})();

export function tenantSlug(): string {
  const fromMeta = document.querySelector('meta[name="tenant-slug"]')?.getAttribute("content");
  const fromEnv = (import.meta.env.VITE_TENANT_SLUG as string | undefined)?.trim();
  return resolveTenantSlug({ fromMeta, fromEnv });
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

function buildUrl(path: string): string {
  if (!API_BASE) return path;
  return `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
}

async function parseBody(res: Response): Promise<unknown> {
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  return null;
}

export async function apiFetch<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: { locale?: "de" | "en" },
): Promise<T> {
  let requestPath = path;
  if (options?.locale) {
    const url = new URL(path, "http://local");
    url.searchParams.set("locale", options.locale);
    requestPath = `${url.pathname}${url.search}`;
  }
  const headers: Record<string, string> = {
    "X-Tenant-Slug": tenantSlug(),
  };
  let payload: BodyInit | undefined;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(buildUrl(requestPath), {
      method,
      headers,
      body: payload,
      credentials: "include",
    });
  } catch (err) {
    observeApiTransportError(err);
    throw err;
  }
  observeApiExchange(res.status);
  if (!res.ok) {
    const parsed = await parseBody(res);
    let message = `${res.status} ${res.statusText}`;
    if (parsed && typeof parsed === "object" && "detail" in parsed) {
      const detail = (parsed as { detail?: unknown }).detail;
      if (typeof detail === "string") message = detail;
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await parseBody(res)) as T;
}

export async function ensureGuestSession(): Promise<boolean> {
  if (loadGuestSession()) return true;
  try {
    await api.get("/api/v1/public/guest/me");
    return true;
  } catch {
    return false;
  }
}

export const api = {
  get: <T>(path: string, options?: { locale?: "de" | "en" }) =>
    apiFetch<T>("GET", path, undefined, options),
  post: <T>(path: string, json?: unknown) => apiFetch<T>("POST", path, json),
  patch: <T>(path: string, json?: unknown) => apiFetch<T>("PATCH", path, json),
  delete: (path: string) => apiFetch<void>("DELETE", path),
};
